import { CalendarMonth } from "@tento-ui/components/calendar-month/calendar-month";
import { CalendarMonthSpan } from "@tento-ui/components/calendar-month/calendar-month-span";
import { NaiveDate } from "@tento-chrono";
import { EventItemPresentation } from "@tento-lona";
import { EventItemAllDay, type Option } from "@tento-lona/sheets";
import type { EventAccess } from "@tento-lona/sheets-ui/sheet/gestures/event-access.ts";

export interface AllDaySpan extends CalendarMonth.Span {
  event: EventItemAllDay;
}

function createAllDaySpan(
  event: EventItemAllDay,
  today: NaiveDate,
  // The client's event presentation (categories, drafts).
  scope: EventItemPresentation.Scope,
  // What the user may do to the event (`EventAccess.of`): a span moves and
  // resizes only when `move` holds; otherwise it shows no resize handles.
  access: EventAccess,
): AllDaySpan {
  const isPastSpan = event.range.end < today.dse;
  const editable = access.move;
  return {
    // Carried identity — the event id, never the date range. Two events
    // sharing a range get distinct keys and never collide in the grid.
    key: event.id,
    // Re-render only when a visibly-rendered field changes.
    contentSig: [
      EventItemPresentation.title(event, scope) || "Untitled",
      isPastSpan
        ? EventItemPresentation.dimmedBackgroundColor(event, scope)
        : EventItemPresentation.backgroundColor(event, scope),
      isPastSpan ? 1 : 0,
      // The resize handles render by editability.
      editable ? 1 : 0,
    ].join("|"),
    range: event.time,
    event,
    isEditable: editable,
    render: (
      info: CalendarMonth.SpanSegmentInfo,
      callbacks?: CalendarMonth.SpanRenderCallbacks,
    ) => {
      const isPast = event.range.end < today.dse;
      const bgColor = isPast
        ? EventItemPresentation.dimmedBackgroundColor(event, scope)
        : EventItemPresentation.backgroundColor(event, scope);
      const title = EventItemPresentation.title(event, scope) || "Untitled";

      // Pass callbacks through so the handle's pointerdown stops propagation
      // and routes resize-edge into `dispatchPointerDown`. Without this the
      // handle is decorative — the host's pointerdown still matches MoveSpan.
      const $span = CalendarMonthSpan.makeWith(
        {
          title,
          color: bgColor,
          segment: info,
          // Handles render on isFirstSegment / isLastSegment internally;
          // gate by editability so read-only events show no grab affordance.
          showResizeHandles: editable,
        },
        callbacks,
      );
      $span.style.setProperty(
        "--text-color",
        isPast ? "var(--text-ter-on-solid)" : "",
      );
      return $span;
    },
  };
}

export namespace AllDaySpan {
  export const make = createAllDaySpan;

  /** The all-day event a month span carries; unset for a span that carries none (the pending one, a ghost). */
  export function eventOf(span: CalendarMonth.Span): Option<EventItemAllDay> {
    return "event" in span && span.event instanceof EventItemAllDay ? span.event : null;
  }
}
